import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { useSearchParams } from 'react-router-dom';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { money } from '../../utils/money.ts';
import { CustomerChips } from '../../components/customer/CustomerChips.tsx';
import { whatsappLink } from '../../components/customer/WhatsAppButton.tsx';
import { errorText, type ShabbatVenue, type VendorMeal, type VendorRegistration } from '../../services/shabbat/shabbatService.ts';
import {
    CombinedBadge, MealTabs, ShabbatFrame, StatusChip, fmtDate, fmtInstant, hasOtherMeals, isCombined, mealName, nowLocal, otherMealText, otherMeals, party,
    NotHostingNote, SincePicker, pickMeal, plural, shabbatService, useSince,
} from './shabbatShared.tsx';


const COLUMNS = 'minmax(220px, 1.7fr) 1fr 1.7fr minmax(190px, 1.3fr) .9fr 1.1fr 170px';

const refundNote = (r: VendorRegistration): string | null => {
    switch (r.refundStatus) {
        case 'CAPTURED': return `Refunded ${money(r.refundAmount ?? r.totalAmount, r.currency)}`;
        case 'AUTHORIZED':
        case 'PENDING': return 'Refund in progress';
        case 'FAILED': return 'Refund failed: Vello follows up';
        default: return null;
    }
};

/**
 * What cancelling this guest from this meal refunds: only this meal's food
 * while the registration keeps another meal; everything still paid (the
 * donation included) when this is its last meal.
 */
const refundIfCancelled = (r: VendorRegistration, mealId: number): number => {
    const others = otherMeals(r, mealId);
    if (others.some(line => line.status === 'ACTIVE')) return r.mealAmount;
    const alreadyRefunded = others.reduce((sum, line) => sum + (line.status === 'CANCELLED' ? line.mealAmount : 0), 0);
    return Math.max(0, (r.paymentTotal ?? r.totalAmount) - alreadyRefunded);
};

/** What this meal's list shows as paid: this meal's share (design v3), or the whole payment from an older backend. */
const paidHere = (r: VendorRegistration): number => r.paidForThisMeal ?? r.totalAmount;
const donationHere = (r: VendorRegistration): number =>
    r.paidForThisMeal != null ? Math.max(0, Math.round((r.paidForThisMeal - r.mealAmount) * 100) / 100) : r.donation;

const cancelledByText: Record<string, string> = { GUEST: 'by the guest', HOST: 'by the host', MEAL_DELETED: 'meal deleted' };

/** One registration on a meal's list. With `venueCell`, a first column naming the venue and meal (all-venues view). */
const RegistrationRow: React.FC<{
    r: VendorRegistration; mealId: number; timeZone: string; started: boolean; columns: string;
    venueCell?: React.ReactNode; onCancel: () => void;
}> = ({ r, mealId, timeZone, started, columns, venueCell, onCancel }) => {
    const note = r.status === 'CANCELLED' ? refundNote(r) : null;
    return (
        <Box sx={{
            display: 'grid', gridTemplateColumns: columns, gap: 1.5, px: 2.25, py: 1.6, alignItems: 'center',
            borderBottom: '1px solid #F0F2F1', fontSize: 14, color: '#111827', '&:hover': { bgcolor: '#FAFBFB' },
            opacity: r.status === 'CANCELLED' ? 0.75 : 1,
        }}>
            {venueCell}
            <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 700, fontSize: 14 }}>{r.bookedBy}</Typography>
                <CustomerChips prev={r.prev} src={r.src} unit="registration">
                    {isCombined(r) && <CombinedBadge />}
                </CustomerChips>
                <Typography sx={{ fontSize: 12, color: '#9CA3AF', mt: 0.4 }}>{r.reference}</Typography>
                {hasOtherMeals(r) && otherMeals(r, mealId).map(line => (
                    <Typography key={line.mealId} sx={{ fontSize: 11.5, color: '#6B7280', mt: 0.25 }}>
                        {otherMealText(line)}
                    </Typography>
                ))}
            </Box>
            <span>{party(r.adults, r.children)}</span>
            <Typography sx={{ fontSize: 13, color: '#4B5563', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {r.guestNames.join(', ')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <span style={{ direction: 'ltr', whiteSpace: 'nowrap', fontSize: 13.5 }}>{r.phone}</span>
                <Tooltip title={`WhatsApp ${r.bookedBy}`}>
                    <IconButton
                        component="a" href={whatsappLink(r.phone)} target="_blank" rel="noopener noreferrer"
                        aria-label={`WhatsApp ${r.bookedBy}`}
                        sx={{ width: 28, height: 28, bgcolor: '#25D366', color: '#fff', '&:hover': { bgcolor: '#1DA851' } }}
                    >
                        <WhatsAppIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{money(paidHere(r), r.currency)}</Typography>
                {donationHere(r) > 0 && <Typography sx={{ fontSize: 12, color: '#03676A' }}>incl. {money(donationHere(r), r.currency)} donation</Typography>}
            </Box>
            <Typography sx={{ fontSize: 13, color: '#4B5563' }}>{fmtInstant(r.createdAt, timeZone)}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                {r.status === 'CONFIRMED' ? (
                    <>
                        <StatusChip label="Confirmed" color="#03676A" bg="#DDF4F1" />
                        {!started && (
                            <Button size="small" color="error" onClick={onCancel} sx={{ minWidth: 0, px: 0.75, fontSize: 12.5 }}>
                                Cancel
                            </Button>
                        )}
                    </>
                ) : (
                    <Box sx={{ textAlign: 'right' }}>
                        <StatusChip label="Cancelled" color="#6B7280" bg="#F3F4F6" />
                        <Typography sx={{ fontSize: 11.5, color: r.refundStatus === 'FAILED' ? '#DC2626' : '#6B7280', mt: 0.5 }}>
                            {[r.cancelledBy ? cancelledByText[r.cancelledBy] : null, note].filter(Boolean).join(' · ')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

/** The cancel dialog: this meal only, its refund, and what the guest keeps. */
const CancelDialog: React.FC<{ cancelling: VendorRegistration | null; meal: VendorMeal | null | undefined; busy: boolean; onKeep: () => void; onCancel: () => void }> = ({ cancelling, meal: m, busy, onKeep, onCancel }) => (
    <Dialog open={!!cancelling} onClose={() => !busy && onKeep()} PaperProps={{ sx: { borderRadius: '16px', maxWidth: 420 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{m ? `Cancel for ${mealName(m.type)}?` : 'Cancel this registration?'}</DialogTitle>
        <DialogContent>
            {cancelling && m && (() => {
                const refund = refundIfCancelled(cancelling, m.id);
                const kept = otherMeals(cancelling, m.id).filter(line => line.status === 'ACTIVE');
                return (
                    <Typography sx={{ fontSize: 14.5, color: '#374151', lineHeight: 1.4 }}>
                        {cancelling.bookedBy} ({plural(cancelling.adults + cancelling.children, 'guest', 'guests')}) will be removed from
                        {' '}{mealName(m.type)}{m.locationName ? ` at ${m.locationName}` : ''} only
                        {refund > 0 ? ` and ${money(refund, cancelling.currency)} refunded to their card` : ''}.
                        {kept.length > 0 && ` Their ${kept.map(line => mealName(line.type)).join(' and ')} booking stays.`}
                        {' '}They get an email.
                    </Typography>
                );
            })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={onKeep} disabled={busy} variant="outlined" sx={{ borderRadius: '20px', color: '#374151', borderColor: '#D1D5DB' }}>Keep</Button>
            <Button onClick={onCancel} disabled={busy} variant="contained" color="error" sx={{ borderRadius: '20px' }}>
                {busy ? 'Cancelling…' : 'Cancel for this meal'}
            </Button>
        </DialogActions>
    </Dialog>
);

type Loaded = { meal: VendorMeal; registrations: VendorRegistration[] };

const VenueRegistrations: React.FC<{ venue: ShabbatVenue; query: string }> = ({ venue, query }) => {
    const notify = useNotify();
    const [params, setParams] = useSearchParams();
    const { from } = useSince();
    const [meals, setMeals] = useState<VendorMeal[] | null>(null);
    const [loaded, setLoaded] = useState<Loaded | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState<VendorRegistration | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        setMeals(null);
        shabbatService.meals(venue.id, from)
            .then(list => { if (alive) setMeals(list); })
            .catch(e => { if (alive) setFailed(errorText(e, 'Could not load the meals.')); });
        return () => { alive = false; };
    }, [venue.id, from]);

    const selected = meals ? pickMeal(meals, Number(params.get('meal')) || null, venue.timeZone) : null;

    const load = useCallback(async (mealId: number) => {
        try {
            setLoaded(await shabbatService.registrations(mealId));
        } catch (e) {
            setFailed(errorText(e, 'Could not load the registrations.'));
        }
    }, []);

    useEffect(() => { setLoaded(null); if (selected) load(selected); }, [selected, load]);

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const all = loaded?.registrations ?? [];
        if (!q) return all;
        const digits = q.replace(/\D/g, '');
        return all.filter(r =>
            [r.bookedBy, r.reference, ...r.guestNames].join(' ').toLowerCase().includes(q)
            || (digits.length >= 3 && r.phone.replace(/\D/g, '').includes(digits)));
    }, [loaded, query]);

    if (failed) return <Alert severity="error" sx={{ maxWidth: 760 }}>{failed}</Alert>;
    if (!meals) return <CircularProgress size={24} />;
    if (meals.length === 0) return (
        <>
            <NotHostingNote venue={venue} />
            <Alert severity="info" sx={{ maxWidth: 640 }}>
                {from ? 'No meals in this period.' : venue.hostsShabbat ? 'No meals yet. Add one on the Shabbat meals page.' : 'No meals this Shabbat or later.'}
            </Alert>
        </>
    );

    const m = loaded?.meal;
    const confirmed = (loaded?.registrations ?? []).filter(r => r.status === 'CONFIRMED');
    const started = m ? `${m.date}T${m.startTime}` <= nowLocal(venue.timeZone) : true;

    const doCancel = async () => {
        if (!cancelling) return;
        setBusy(true);
        try {
            await shabbatService.cancelRegistration(cancelling.id, m?.id);
            const keepsOther = !!m && otherMeals(cancelling, m.id).some(line => line.status === 'ACTIVE');
            notify(keepsOther
                ? `${cancelling.reference} cancelled for this meal. Refund issued; their other meal stays booked.`
                : `Registration ${cancelling.reference} cancelled. Refund issued.`, { type: 'success' });
            setCancelling(null);
            if (selected) await load(selected);
        } catch (e) {
            notify(errorText(e, 'The registration could not be cancelled. Try again.'), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <NotHostingNote venue={venue} />
            <MealTabs meals={meals} selected={selected} onPick={id => setParams(p => { const next = new URLSearchParams(p); next.set('meal', String(id)); return next; })} />

            {!loaded && <Box sx={{ mt: 3 }}><CircularProgress size={24} /></Box>}

            {loaded && m && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25, mt: 2.25, fontSize: 14, color: '#374151', flexWrap: 'wrap' }}>
                        <Typography component="span" sx={{ fontWeight: 700, color: '#111827' }}>{plural(m.guests, 'guest', 'guests')}</Typography>
                        <span>{plural(m.registrations, 'registration', 'registrations')}</span>
                        <span>{money(m.collected, m.currency)} collected</span>
                        <Typography component="span" sx={{ color: '#03676A' }}>{plural(m.seatsLeft, 'seat left', 'seats left')}</Typography>
                    </Box>

                    <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', mt: 1.75, overflowX: 'auto' }}>
                        <Box sx={{ minWidth: 980 }}>
                            <Box sx={{
                                display: 'grid', gridTemplateColumns: COLUMNS, gap: 1.5, px: 2.25, py: 1.5, fontSize: 12, fontWeight: 700,
                                color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFBFB',
                            }}>
                                <span>Booked by</span><span>Party</span><span>Guest names</span><span>Phone / WhatsApp</span><span>Paid</span><span>Registered</span><span />
                            </Box>
                            {rows.length === 0 && (
                                <Typography sx={{ p: 5, textAlign: 'center', fontSize: 14, color: '#6B7280' }}>
                                    {loaded.registrations.length === 0 ? 'No registrations yet.' : 'No registrations match.'}
                                </Typography>
                            )}
                            {rows.map(r => (
                                <RegistrationRow key={r.id} r={r} mealId={m.id} timeZone={venue.timeZone} started={started}
                                    columns={COLUMNS} onCancel={() => setCancelling(r)} />
                            ))}
                        </Box>
                    </Box>
                    {confirmed.length > 0 && (
                        <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 1.5 }}>
                            {confirmed.some(r => r.prev != null) && 'NC = new customer, RC n = returning with n earlier Shabbat registrations on Vello (any venue). '}
                            Guests paid in the Vello app. Cancel removes the guest from this meal only, refunds it to their card and emails them.
                            A guest booked for both meals keeps the other one; their donation is refunded only when no meal is left.
                        </Typography>
                    )}
                </>
            )}

            <CancelDialog cancelling={cancelling} meal={m} busy={busy} onKeep={() => setCancelling(null)} onCancel={doCancel} />
        </>
    );
};

const ALL_COLUMNS = 'minmax(170px, 1.1fr) ' + COLUMNS;

/** Meals of every venue grouped by day and kind: "Friday night dinner · Fri 23 Oct". */
const slotOf = (m: VendorMeal) => `${m.type}|${m.date}`;
const slotLabel = (m: VendorMeal) => `${mealName(m.type)} · ${fmtDate(m.date)}`;

/**
 * Every venue's registrations together (an admin's default, 23 Oct 2026):
 * one row per registration per meal, the venue and meal in the first column.
 * Tabs group the meals by day - every Friday dinner of all venues, and so on.
 */
const AllRegistrations: React.FC<{ query: string }> = ({ query }) => {
    const notify = useNotify();
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState<{ meals: VendorMeal[]; registrations: VendorRegistration[] } | null>(null);
    const [failed, setFailed] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState<VendorRegistration | null>(null);
    const [busy, setBusy] = useState(false);
    const { from } = useSince();

    const load = useCallback(async () => {
        try {
            setData(await shabbatService.registrationsInView(null, from));
            setFailed(null);
        } catch (e) {
            setFailed(errorText(e, 'Could not load the registrations.'));
        }
    }, [from]);
    useEffect(() => { load(); }, [load]);

    const slot = params.get('slot');
    const meals = useMemo(() => data?.meals ?? [], [data]);
    const slots = useMemo(() => {
        const seen = new Map<string, string>();
        meals.forEach(m => { if (!seen.has(slotOf(m))) seen.set(slotOf(m), slotLabel(m)); });
        return Array.from(seen.entries());
    }, [meals]);
    const inSlot = meals.filter(m => !slot || slotOf(m) === slot);
    const mealById = useMemo(() => new Map(meals.map(m => [m.id, m])), [meals]);

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const digits = q.replace(/\D/g, '');
        return (data?.registrations ?? []).filter(r => {
            const m = r.mealId ? mealById.get(r.mealId) : undefined;
            if (slot && (!m || slotOf(m) !== slot)) return false;
            if (!q) return true;
            return [r.bookedBy, r.reference, r.venueName ?? '', ...r.guestNames].join(' ').toLowerCase().includes(q)
                || (digits.length >= 3 && r.phone.replace(/\D/g, '').includes(digits));
        });
    }, [data, query, slot, mealById]);

    if (failed) return <Alert severity="error" sx={{ maxWidth: 760 }}>{failed}</Alert>;
    if (!data) return <CircularProgress size={24} />;
    if (meals.length === 0) return <Alert severity="info" sx={{ maxWidth: 640 }}>{from ? 'No venue had meals in this period.' : 'No venue has meals this Shabbat or later.'}</Alert>;

    const pick = (next: string | null) => setParams(p => {
        const n = new URLSearchParams(p);
        if (next) n.set('slot', next); else n.delete('slot');
        return n;
    });
    const cancellingMeal = cancelling?.mealId ? mealById.get(cancelling.mealId) : null;
    const sum = (f: (m: VendorMeal) => number) => inSlot.reduce((a, m) => a + f(m), 0);

    const doCancel = async () => {
        if (!cancelling || !cancellingMeal) return;
        setBusy(true);
        try {
            await shabbatService.cancelRegistration(cancelling.id, cancellingMeal.id);
            const keepsOther = otherMeals(cancelling, cancellingMeal.id).some(line => line.status === 'ACTIVE');
            notify(keepsOther
                ? `${cancelling.reference} cancelled for this meal. Refund issued; their other meal stays booked.`
                : `Registration ${cancelling.reference} cancelled. Refund issued.`, { type: 'success' });
            setCancelling(null);
            await load();
        } catch (e) {
            notify(errorText(e, 'The registration could not be cancelled. Try again.'), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const tab = (key: string | null, label: string) => {
        const on = (slot ?? null) === key;
        return (
            <Box key={key ?? 'all'} component="button" type="button" onClick={() => pick(key)} sx={{
                height: 36, borderRadius: '18px', px: 2, fontSize: 14, cursor: on ? 'default' : 'pointer',
                fontFamily: 'inherit', fontWeight: on ? 700 : 400,
                bgcolor: on ? '#24BBAA' : '#fff', color: on ? '#fff' : '#374151',
                border: on ? '1px solid #24BBAA' : '1px solid #E5E7EB', '&:hover': on ? {} : { borderColor: '#24BBAA' },
            }}>{label}</Box>
        );
    };

    return (
        <>
            <Box data-noprint="1" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {tab(null, 'All meals')}
                {slots.map(([key, label]) => tab(key, label))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25, mt: 2.25, fontSize: 14, color: '#374151', flexWrap: 'wrap' }}>
                <Typography component="span" sx={{ fontWeight: 700, color: '#111827' }}>{plural(sum(m => m.guests), 'guest', 'guests')}</Typography>
                <span>{plural(new Set(rows.filter(r => r.status === 'CONFIRMED').map(r => r.id)).size, 'registration', 'registrations')}</span>
                <span>{money(sum(m => Number(m.collected) || 0), 'EUR')} collected</span>
                <Typography component="span" sx={{ color: '#03676A' }}>{plural(sum(m => m.seatsLeft), 'seat left', 'seats left')}</Typography>
                <span>{plural(new Set(inSlot.map(m => m.locationId)).size, 'venue', 'venues')} · {plural(inSlot.length, 'meal', 'meals')}</span>
            </Box>

            <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', mt: 1.75, overflowX: 'auto' }}>
                <Box sx={{ minWidth: 1160 }}>
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: ALL_COLUMNS, gap: 1.5, px: 2.25, py: 1.5, fontSize: 12, fontWeight: 700,
                        color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFBFB',
                    }}>
                        <span>Venue</span><span>Booked by</span><span>Party</span><span>Guest names</span><span>Phone / WhatsApp</span><span>Paid</span><span>Registered</span><span />
                    </Box>
                    {rows.length === 0 && (
                        <Typography sx={{ p: 5, textAlign: 'center', fontSize: 14, color: '#6B7280' }}>
                            {(data.registrations.length === 0 ? 'No registrations yet.' : 'No registrations match.')}
                        </Typography>
                    )}
                    {rows.map(r => {
                        const m = r.mealId ? mealById.get(r.mealId) : undefined;
                        const tz = m?.timeZone ?? 'Asia/Nicosia';
                        const started = m ? `${m.date}T${m.startTime}` <= nowLocal(tz) : true;
                        return (
                            <RegistrationRow key={`${r.id}-${r.mealId}`} r={r} mealId={r.mealId ?? 0} timeZone={tz} started={started}
                                columns={ALL_COLUMNS} onCancel={() => setCancelling(r)}
                                venueCell={(
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography noWrap sx={{ fontWeight: 700, fontSize: 13.5, color: '#03676A' }}>{r.venueName ?? m?.locationName ?? '—'}</Typography>
                                        {m && <Typography sx={{ fontSize: 12, color: '#6B7280' }}>{mealName(m.type)} · {fmtDate(m.date)} · {m.startTime}</Typography>}
                                    </Box>
                                )} />
                        );
                    })}
                </Box>
            </Box>
            {rows.some(r => r.status === 'CONFIRMED') && (
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 1.5 }}>
                    {rows.some(r => r.prev != null) && 'NC = new customer, RC n = returning with n earlier Shabbat registrations on Vello (any venue). '}
                    Pick a venue above to see one venue's meals. Cancel removes the guest from that meal only, refunds it to their card and emails them.
                </Typography>
            )}
            <CancelDialog cancelling={cancelling} meal={cancellingMeal} busy={busy} onKeep={() => setCancelling(null)} onCancel={doCancel} />
        </>
    );
};

/** Who registered for each meal, with WhatsApp links and cancel-with-refund. Admins start on all venues. */
const ShabbatRegistrationsPage = () => {
    const notify = useNotify();
    const [query, setQuery] = useState('');
    const [params] = useSearchParams();
    const { from } = useSince();

    // WhatsApp has no broadcast link, so "Message all" copies every confirmed
    // number for pasting into a WhatsApp broadcast list or group: this meal's,
    // or on the all-venues view every meal in the chosen tab.
    const copyAll = async (venue: ShabbatVenue | null) => {
        try {
            let confirmed: VendorRegistration[];
            if (venue) {
                const meals = await shabbatService.meals(venue.id, from);
                const mealId = pickMeal(meals, Number(params.get('meal')) || null, venue.timeZone);
                if (!mealId) return;
                confirmed = (await shabbatService.registrations(mealId)).registrations;
            } else {
                const all = await shabbatService.registrationsInView(null, from);
                const slot = params.get('slot');
                const ids = new Set(all.meals.filter(m => !slot || slotOf(m) === slot).map(m => m.id));
                confirmed = all.registrations.filter(r => r.mealId && ids.has(r.mealId));
            }
            const phones = Array.from(new Set(confirmed.filter(r => r.status === 'CONFIRMED').map(r => r.phone)));
            if (phones.length === 0) { notify('Nobody is registered yet', { type: 'info' }); return; }
            await navigator.clipboard.writeText(phones.join('\n'));
            notify(`${plural(phones.length, 'phone number', 'phone numbers')} copied. Paste them into a WhatsApp broadcast list.`, { type: 'success' });
        } catch (e) {
            notify(errorText(e, 'Could not copy the numbers.'), { type: 'error' });
        }
    };

    return (
        <ShabbatFrame
            title="Registrations"
            subtitle="Everyone who registered through the Vello app"
            allowAll
            actions={(venue) => (
                <>
                    <SincePicker />
                    <TextField
                        size="small" type="search" placeholder="Search guests or phone" value={query} onChange={e => setQuery(e.target.value)}
                        inputProps={{ 'aria-label': 'Search guests or phone' }}
                        sx={{ width: 260, maxWidth: '100%', '& .MuiInputBase-input': { borderRadius: '20px !important', paddingLeft: '16px !important' } }}
                    />
                    <Button
                        variant="contained" startIcon={<WhatsAppIcon />} onClick={() => copyAll(venue)}
                        sx={{ borderRadius: '20px', bgcolor: '#25D366', color: '#fff', height: 40, px: 2, '&:hover': { bgcolor: '#1DA851' } }}
                    >
                        Message all
                    </Button>
                </>
            )}
        >
            {(venue) => venue
                ? <VenueRegistrations key={venue.id} venue={venue} query={query} />
                : <AllRegistrations query={query} />}
        </ShabbatFrame>
    );
};

export default ShabbatRegistrationsPage;
